// Touring Industries v1: minimal frontend JS

class VariantPicker {
  constructor(form) {
    this.form = form;
    const jsonEl = form.querySelector('[data-product-json]');
    if (!jsonEl) return;
    try {
      this.productJson = JSON.parse(jsonEl.textContent);
    } catch (e) { console.warn('VariantPicker JSON parse failed', e); return; }
    this.variants = this.productJson.variants;
    const scope = form.closest('.product-info') || document;
    this.priceTarget = scope.querySelector('[data-price]');
    this.priceCompareTarget = scope.querySelector('[data-compare]');
    this.stockTarget = scope.querySelector('[data-stock-indicator]');
    this.addButton = form.querySelector('[data-add-button]');
    this.variantInput = form.querySelector('[name="id"]');
    this.optionInputs = form.querySelectorAll('[data-option-input]');
    this.addonInputs = form.querySelectorAll('[data-addon-input]');
    this.packInputs = form.querySelectorAll('[data-pack-input]');
    this.optionInputs.forEach(input => input.addEventListener('change', () => this.update()));
    this.addonInputs.forEach(input => input.addEventListener('change', () => this.update()));
    this.packInputs.forEach(input => input.addEventListener('change', () => this.update()));
    this.form.addEventListener('submit', (e) => { e.preventDefault(); this.addToCart(); });
    this.update();
  }

  selectedOptions() {
    const byName = {};
    this.optionInputs.forEach(input => {
      if (input.type === 'radio') { if (input.checked) byName[input.name] = input.value; }
      else { byName[input.name] = input.value; }
    });
    return Object.values(byName);
  }

  findMatch() {
    if (this.optionInputs.length === 0) return this.variants[0];
    const selected = this.selectedOptions();
    return this.variants.find(v =>
      v.options.length === selected.length &&
      v.options.every((opt, i) => opt === selected[i])
    );
  }

  // Current selection: a Pack option (Plug Only / Combo) if present, else the matched variant.
  getSelection() {
    if (this.packInputs && this.packInputs.length) {
      const sel = [...this.packInputs].find(i => i.checked) || this.packInputs[0];
      return {
        id: parseInt(sel.value, 10),
        price: parseInt(sel.dataset.packPrice || '0', 10),
        available: sel.dataset.packAvailable === 'true',
        isPack: true
      };
    }
    const m = this.findMatch();
    return m ? { id: m.id, price: m.price, available: m.available, compare_at_price: m.compare_at_price, inventory_management: m.inventory_management, inventory_quantity: m.inventory_quantity } : null;
  }

  addonTotal() {
    let total = 0;
    this.addonInputs.forEach(inp => {
      if (inp.checked && inp.value) total += parseInt(inp.dataset.addonPrice || '0', 10);
    });
    return total;
  }

  selectedAddonVariantIds() {
    const ids = [];
    this.addonInputs.forEach(inp => { if (inp.checked && inp.value) ids.push(parseInt(inp.value, 10)); });
    return ids;
  }

  toggleEngravingText() {
    this.form.querySelectorAll('[data-engraving-text]').forEach(t => {
      const grp = t.dataset.engravingGroup;
      const sel = this.form.querySelector(`input[name="${grp}"]:checked`);
      const show = !!(sel && sel.value);
      const kind = sel ? (sel.dataset.engravingKind || '') : '';
      const isLogo = /logo/.test(kind);
      t.style.display = show ? '' : 'none';
      t.disabled = !show;
      if (!show) t.value = '';
      t.placeholder = isLogo ? 'Logo engraving notes (optional)' : 'What should we engrave?';
      const note = this.form.querySelector(`[data-engraving-note][data-engraving-group="${grp}"]`);
      if (note) note.style.display = (show && isLogo) ? '' : 'none';
    });
  }

  lineItemProperties() {
    const props = {};
    this.form.querySelectorAll('[name^="properties["]').forEach(el => {
      if (el.disabled) return;
      const m = el.name.match(/^properties\[(.+)\]$/);
      if (!m) return;
      const val = (el.value || '').trim();
      if (val) props[m[1]] = val;
    });
    return props;
  }

  setStock(match) {
    if (!this.stockTarget) return;
    const el = this.stockTarget;
    el.className = 'stock-indicator';
    if (!match) { el.innerHTML = ''; return; }
    if (!match.available) { el.classList.add('out'); el.innerHTML = '<span class="dot"></span>Sold out'; return; }
    const mgmt = match.inventory_management;
    const qty = match.inventory_quantity;
    if (mgmt === 'shopify' && qty > 0 && qty <= 10) { el.classList.add('low'); el.innerHTML = '<span class="dot"></span>Only ' + qty + ' left in stock'; }
    else if (mgmt === 'shopify' && qty > 10) { el.classList.add('in'); el.innerHTML = '<span class="dot"></span>In stock'; }
    else if (!mgmt) { el.innerHTML = ''; }
    else { el.classList.add('made'); el.innerHTML = '<span class="dot"></span>Made to order'; }
  }

  setStockSimple(available) {
    if (!this.stockTarget) return;
    const el = this.stockTarget;
    el.className = 'stock-indicator';
    if (available) { el.classList.add('in'); el.innerHTML = '<span class="dot"></span>In stock'; }
    else { el.classList.add('out'); el.innerHTML = '<span class="dot"></span>Sold out'; }
  }

  update() {
    this.optionInputs.forEach(input => {
      const label = input.closest('.option-value');
      if (label && input.type === 'radio') label.classList.toggle('selected', input.checked);
    });
    this.addonInputs.forEach(input => {
      const label = input.closest('.option-value');
      if (label) label.classList.toggle('selected', input.checked);
    });
    this.packInputs.forEach(input => {
      const label = input.closest('.option-value');
      if (label) label.classList.toggle('selected', input.checked);
    });
    const packLabel = this.form.querySelector('[data-pack-label]');
    if (packLabel && this.packInputs.length) {
      const ps = [...this.packInputs].find(i => i.checked);
      if (ps) packLabel.textContent = ps.dataset.packName || '';
    }
    this.form.querySelectorAll('[data-option-label]').forEach(span => {
      const idx = span.dataset.optionLabel;
      const checked = this.form.querySelector(`input[name="option-${idx}"]:checked`);
      if (checked) span.textContent = checked.value;
    });
    this.toggleEngravingText();

    const sel = this.getSelection();
    if (!sel) {
      this.addButton.disabled = true;
      this.addButton.textContent = 'Unavailable';
      this.setStock(null);
      return;
    }
    this.variantInput.value = sel.id;
    const addon = this.addonTotal();
    if (this.priceTarget) {
      this.priceTarget.textContent = addon > 0
        ? this.formatPrice(sel.price) + ' + ' + this.formatPrice(addon)
        : this.formatPrice(sel.price);
    }
    if (this.priceCompareTarget) {
      if (sel.compare_at_price && sel.compare_at_price > sel.price) {
        this.priceCompareTarget.textContent = this.formatPrice(sel.compare_at_price);
        this.priceCompareTarget.style.display = '';
      } else {
        this.priceCompareTarget.style.display = 'none';
      }
    }
    if (sel.isPack) this.setStockSimple(sel.available);
    else this.setStock(sel);
    this.addButton.disabled = !sel.available;
    this.addButton.textContent = sel.available ? 'Add to cart' : 'Sold out';
  }

  addToCart() {
    const sel = this.getSelection();
    if (!sel || !sel.available) return;
    const orig = this.addButton.textContent;
    this.addButton.disabled = true;
    this.addButton.textContent = 'Adding…';
    const items = [{ id: sel.id, quantity: 1, properties: this.lineItemProperties() }];
    this.selectedAddonVariantIds().forEach(id => items.push({ id: id, quantity: 1, properties: { '_addon': 'yes' } }));
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw e; }); return r.json(); })
      .then(() => { window.location = '/cart'; })
      .catch(err => {
        this.addButton.disabled = false;
        this.addButton.textContent = orig;
        alert((err && err.description) || 'Sorry, that could not be added to cart.');
      });
  }

  formatPrice(cents) { return '$' + (cents / 100).toFixed(2); }
}

class ProductGallery {
  constructor(root) {
    this.root = root;
    this.main = root.querySelector('[data-gallery-main] img');
    this.thumbs = root.querySelectorAll('[data-gallery-thumb]');
    if (!this.main) return;
    if (window.requestIdleCallback) requestIdleCallback(() => this.preload());
    else setTimeout(() => this.preload(), 500);
    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        const src = thumb.dataset.src;
        if (src) {
          this.main.src = src;
          this.thumbs.forEach(t => t.setAttribute('aria-current', 'false'));
          thumb.setAttribute('aria-current', 'true');
        }
      });
    });
  }
  preload() {
    this.thumbs.forEach(t => { if (t.dataset.src) { const i = new Image(); i.src = t.dataset.src; } });
  }
}

function initMobileNav() {
  const toggle = document.querySelector('.menu-toggle');
  const nav = document.querySelector('.site-nav');
  if (!toggle || !nav) return;
  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    const isOpen = nav.classList.toggle('open');
    toggle.setAttribute('aria-expanded', isOpen);
    document.body.classList.toggle('nav-open', isOpen);
  });
  nav.querySelectorAll('a').forEach(a => a.addEventListener('click', () => {
    nav.classList.remove('open');
    document.body.classList.remove('nav-open');
    toggle.setAttribute('aria-expanded', 'false');
  }));
  document.addEventListener('click', (e) => {
    if (nav.classList.contains('open') && !nav.contains(e.target) && e.target !== toggle) {
      nav.classList.remove('open');
      document.body.classList.remove('nav-open');
      toggle.setAttribute('aria-expanded', 'false');
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && nav.classList.contains('open')) {
      nav.classList.remove('open');
      document.body.classList.remove('nav-open');
      toggle.setAttribute('aria-expanded', 'false');
    }
  });
}

async function refreshCartCount() {
  try {
    const r = await fetch('/cart.js');
    const cart = await r.json();
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? '' : 'none';
    });
  } catch (e) {}
}

function init() {
  document.querySelectorAll('[data-variant-picker]').forEach(form => new VariantPicker(form));
  document.querySelectorAll('[data-product-gallery]').forEach(root => new ProductGallery(root));
  initMobileNav();
  refreshCartCount();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
